import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "@fluentui/react-components";
import {
  useAuth,
  fetchProperties,
  fetchInitialData,
  pollUserMessage,
  postUserMessage,
  postFeedback,
  ChatMessage,
  TextDialog,
  ComposeBox,
  formatEmailText,
  cleanDraftText,
  markdownToHtml,
  formatUserMessage,
  htmlToText,
  mainStyle,
  useMessages,
  postUsage,
  postUserTone,
  selectUserStyle,
  getEmailConversationThread,
  formatConversationContext,
  testGraphAPIAccess,
  requestAttachmentByReference,
  requestShareableLinkByReference,
  useFileContext,
  useFileUpload,
  buildFileUsageMeta,
  openResourceView,
  buildResourceReference,
} from "common";

import {
  extractSubjectAndContent,
} from "../utils/outlookUtils";
import "./Scrollbar.css";

import PropTypes from "prop-types";
import { getCachedContext, setCachedContext, getCachedInstruction, setCachedInstruction, clearCachedInstruction } from "../utils/contextCache";
import {
  setCachedChatSessionForItem,
  clearCachedChatSessionForItem,
  serializeMessagesForCache,
  resolveChatSession,
  clearDraftContextForItem,
  hasDraftContextForItem,
} from "../utils/chatSessionCache";
import { getItemContextKey } from "../utils/itemContextKey";
import { beginFetchDataRun } from "../utils/outlookLoadDebug";
import { loadReadModeEmailBodyText } from "../utils/emailBodyLoader";
import { acquireGraphAPIToken } from "../utils/graphToken";
import { isVstoHost } from "../../host";

const UnicliChat = ({ host, currentItem, currentUser, graphAPIToken, onGraphTokenAcquired }) => {
  const item = currentItem || host.getSnapshot();
  const [isComposeReplyWindow, setIsComposeReplyWindow] = useState(false);
  const isComposeContext = host.isComposeMode() || !!item?.isCompose || isComposeReplyWindow;
  const isReplyWindow = !isComposeContext;
  const vstoMode = isVstoHost(host);

  const [residents, setResidents] = useState([]);
  const [properties, setProperties] = useState([]);
  const [userInput, setUserInput] = useState("");
  const hasFetchedInitialData = useRef(false);
  const userPromptArea = useRef(null);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const selectedOptionsRef = useRef(selectedOptions);
  const [selectedValue, setSelectedValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(0);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState(isReplyWindow ? "replyall" : "new");
  const [feedbackMessageId, setFeedbackMessageId] = useState(0); // Message ID for feedback
  const [checkedTone, setCheckedTone] = useState(currentUser?.tone || "Casual");
  const lastMessageRef = useRef(null);
  const [searchChecked, setSearchChecked] = useState(true);
  const [userStyles, setUserStyles] = useState(Array.isArray(currentUser?.style) ? currentUser.style : []);
  const [selectedStyleName, setSelectedStyleName] = useState(() => {
    const selected = (Array.isArray(currentUser?.style) ? currentUser.style : []).find((s) => s.selected);
    return selected?.name || (Array.isArray(currentUser?.style) && currentUser.style[0]?.name) || "Default";
  });
  const [selectedStyleContent, setSelectedStyleContent] = useState(() => {
    const selected = (Array.isArray(currentUser?.style) ? currentUser.style : []).find((s) => s.selected);
    return selected?.content || "";
  });
  useEffect(() => {
    const stylesArr = Array.isArray(currentUser?.style) ? currentUser.style : [];
    setUserStyles(stylesArr);
    const selected = stylesArr.find((s) => s.selected);
    setSelectedStyleName(selected?.name || (stylesArr[0]?.name) || "Default");
    setSelectedStyleContent(selected?.content || "");
  }, [currentUser]);
  const { authState, login, logout } = useAuth();
  const fileContext = useFileContext(authState?.authToken, selectedOptions[0]);
  const { getFileIds, runWithFileValidation } = fileContext;
  const styles = mainStyle();
  // Loader text from poll status
  const [loadingStageText, setLoadingStageText] = useState("");
  const handlePollProgress = (statusData) => {
    if (statusData?.message) {
      setLoadingStageText(statusData.message);
    }
  };
  const [hasSelectionForRewrite, setHasSelectionForRewrite] = useState(false);
  const persistGuardRef = useRef(false);

  const getMessageKey = () => {
    try {
      return host.getMessageKey();
    } catch {
      return "";
    }
  };

  const contextMessageKey = getItemContextKey(currentItem);

  // Helper function to check if we can use Graph API
  const canUseGraphAPI = () => {
    if (vstoMode || !host.supportsGraph()) {
      return false;
    }
    // First check if we have the Graph API token from prop
    if (graphAPIToken) {
      console.log("Checking if we can use Graph API - graphAPIToken from prop available:", !!graphAPIToken);
      return true;
    }

    // Fallback: check if we have it stored in auth state
    if (authState?.graphAPIToken) {
      console.log("Checking if we can use Graph API - graphAPIToken from auth state available:", !!authState.graphAPIToken);
      return true;
    }

    console.log("Checking if we can use Graph API - no graphAPIToken available");
    return false;
  };

  // Helper function to get the Graph API token
  const getGraphAPIToken = () => {
    return graphAPIToken || authState?.graphAPIToken;
  };

  useEffect(() => {
    if (!item?.isCompose && !host.isComposeMode()) {
      return;
    }
    if (item?.inReplyTo || hasDraftContextForItem(item)) {
      setIsComposeReplyWindow(true);
      return;
    }
    host
      .getSubject()
      .then((subject) => {
        if (/^(RE:|FW:|Re:|Fw:)/.test(subject || "")) {
          setIsComposeReplyWindow(true);
        }
      })
      .catch(() => {});
  }, [item?.isCompose, item?.inReplyTo, item?.conversationId, host]);

  const handleAttachReference = async (reference) => {
    if (!isComposeContext) return;
    const { download_url: downloadUrl, filename } = await requestAttachmentByReference(reference, authState.authToken);
    await host.attachFileFromUrl(downloadUrl, filename || reference.name || "attachment");
    try {
      const meta = {
        mode: "compose",
        reply: false,
        detail: reference?.name || "",
        source: "doutlookcompose",
        tokens: 0,
        property_id: 0,
        prompt_tokens: 0,
        completion_tokens: 0,
      };
      onPostUsage("email-attach", "add-in-use", meta);
    } catch (e) {
      console.warn("Failed to post usage for attach:", e);
    }
  };

  // Get shareable link and copy to clipboard
  const handleShareReference = async (reference) => {
    const { webUrl } = await requestShareableLinkByReference(reference, authState.authToken);
    // Try async clipboard first
    try {
      await navigator.clipboard.writeText(webUrl);
      try {
        const meta = {
          mode: "compose",
          reply: false,
          detail: reference?.name || "",
          source: "doutlookcompose",
          tokens: 0,
          property_id: 0,
          prompt_tokens: 0,
          completion_tokens: 0,
        };
        onPostUsage("email-shareLink", "add-in-use", meta);
      } catch (e) {
        console.warn("Failed to post usage for share link:", e);
      }
      return { copied: true };
    } catch (err) {
      // Fallback to execCommand('copy')
      try {
        const success = (() => {
          const el = document.createElement("textarea");
          el.value = webUrl;
          el.setAttribute("readonly", "");
          el.style.position = "fixed";
          el.style.opacity = "0";
          document.body.appendChild(el);
          el.focus();
          el.select();
          el.setSelectionRange(0, 99999);
          let ok = false;
          try {
            ok = document.execCommand("copy");
          } catch (e) {
            ok = false;
          }
          document.body.removeChild(el);
          return ok;
        })();
        if (success) {
          // Log usage even if async clipboard was blocked
          try {
            const meta = {
              mode: "compose",
              reply: false,
              detail: reference?.name || "",
              source: "doutlookcompose",
              tokens: 0,
              property_id: 0,
              prompt_tokens: 0,
              completion_tokens: 0,
            };
            onPostUsage("email-shareLink", "add-in-use", meta);
          } catch (e) {
            console.warn("Failed to post usage for share link:", e);
          }
          // Signal 'Copied!' in UI for execCommand fallback success
          return { copied: true };
        }
      } catch (fallbackErr) {
        // continue to final fallback
      }
      // Final fallback: post bot message with the link in chat
      try {
        const safeName = reference?.name || "document";
        const text = `Here's the shareable link for ${safeName}:<br/>${webUrl}`;
        addMessage(text, "bot");
      } catch (e) {
        console.warn("Failed to add fallback bot message:", e);
      }
      return { copied: false };
    }
  };


  // Debug function to test Graph API token (can be called from browser console)
  const testGraphAPIDebug = async () => {
    console.log("=== Graph API Debug Test ===");

    const currentGraphAPIToken = getGraphAPIToken();
    console.log("1. Graph API token available:", currentGraphAPIToken ? "Available" : "Not available");

    if (currentGraphAPIToken) {
      console.log("2. Token preview:", currentGraphAPIToken.substring(0, 50) + "...");

      try {
        console.log("3. Testing Graph API access...");
        const graphTest = await testGraphAPIAccess(currentGraphAPIToken);
        console.log("4. Graph API access test result:", graphTest ? "Success" : "Failed");

        if (graphTest) {
          console.log("5. Testing message retrieval...");
          const testResponse = await fetch("https://graph.microsoft.com/v1.0/me/messages?$top=1", {
            headers: {
              Authorization: `Bearer ${currentGraphAPIToken}`,
              "Content-Type": "application/json",
            },
          });
          console.log("6. Message retrieval test:", testResponse.ok ? "Success" : "Failed");
        }
      } catch (error) {
        console.error("Debug test failed:", error);
      }
    }

    console.log("=== End Graph API Debug Test ===");
  };

  // Make the debug function available globally for testing
  if (typeof window !== "undefined") {
    window.testGraphAPIDebug = testGraphAPIDebug;
  }

  const processFeedback = (messageId, feedback) => {
    if (feedback === "down") {
      setFeedbackMessageId(messageId);
      handleOpenFeedback();
    } else {
      handleFeedback(messageId, feedback);
    }
  };

  const { messages, addMessage, resetMessages, restoreMessages } = useMessages(processFeedback);

  const handleCloseFeedback = () => setIsFeedbackOpen(false);
  const handleOpenFeedback = () => setIsFeedbackOpen(true);

  const clearInstructionInput = () => {
    setUserInput("");
    try {
      const keys = new Set(
        [getMessageKey(), contextMessageKey, item?.internetMessageId, item?.inReplyTo].filter(Boolean)
      );
      keys.forEach((key) => clearCachedInstruction(key));
    } catch {
      // ignore
    }
  };

  const handleSendMessage = async (event) => {
    const executeSend = () => {
      if (selectedTab === "chat") {
        if (!userInput.trim()) {
          return;
        }
        setIsLoading(true);
        addMessage(formatUserMessage(userInput, selectedTab), "user");
        const messageToSend = userInput;
        clearInstructionInput();
        sendToBot(messageToSend);
        return;
      }
      const draftInstruction = userInput;
      addMessage(formatUserMessage(draftInstruction, selectedTab), "user");
      clearInstructionInput();
      handleDraft(draftInstruction);
    };

    await runWithFileValidation(executeSend, { onDocContext: postDocContextUsage });
    event.currentTarget.blur();
  };

  //TBD: remove this function later
  // The functionality moved to server
  function linkifyFilenames(content, references) {
    if (!references || references.length === 0) {
      return content;
    }
    // Helper function to normalize names by collapsing multiple spaces
    const normalizeName = (name) => name.replace(/\s+/g, " ").trim();

    // Replace file names with links by iterating through the references
    let linkedContent = content; // Keep the original content

    references.forEach((reference) => {
      const normalizedFileName = normalizeName(reference.name);

      // Check if the normalized file name exists in the content
      if (linkedContent.includes(normalizedFileName)) {
        // Define the inline style as a string
        const linkStyle =
          "font-family: 'Segoe UI'; font-style: normal; font-weight: 400; font-size: 10px; line-height: 10px; color: #0F6CBD; text-decoration: none;";

        // Create the anchor tag
        const anchorTag = `<a href="${reference.url}" target="_blank" style="${linkStyle}">${normalizedFileName}</a>`;

        // Replace the normalized file name with the anchor tag in the content
        linkedContent = linkedContent.replaceAll(normalizedFileName, anchorTag);
      }
    });

    return linkedContent;
  }

  const handleBotResponse = (response, references, messageId) => {
    console.log("Bot response:", response);
    // response = markdownToHtml(response);
    // response = linkifyFilenames(response, references);
    addMessage(response, "bot", { links: references, buttions: [], messageId: messageId });
    setIsLoading(false);
    clearInstructionInput();
  };
  const onSearchCheckedChange = React.useCallback(
    (ev) => {
      console.log("Search checked:", ev.currentTarget.checked);
      setSearchChecked(ev.currentTarget.checked);
    },
    [searchChecked]
  );
  const onSignout = () => {
    // Clear the auth state
    logout();
  };

  const updateUserStyle = async (styleName) => {
    try {
      const data = await selectUserStyle(authState.authToken, styleName);
      console.log("User style selected successfully", data);
      // Update local selection state
      setUserStyles((prev) => {
        const updated = (prev || []).map((s) => ({ ...s, selected: s.name === styleName }));
        const sel = updated.find((s) => s.selected);
        setSelectedStyleName(sel?.name || styleName);
        setSelectedStyleContent(sel?.content || "");
        return updated;
      });
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      }
      console.error("Failed to select user style:", error);
    }
  };

  const sendToBot = async (userMessage) => {
    try {
      const isCompose = host.isComposeMode();
      const bodyText = await host.getBodyText("sendToBot");
      const data = await postUserMessage(
        selectedOptions[0],
        conversationId,
        userMessage,
        authState.authToken,
        isCompose ? "outlookcompose" : "outlookreply",
        "chat",
        bodyText,
        isCompose ? host.getUserDisplayName() : item.sender?.displayName,
        item.to?.[0]?.displayName || "",
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );
      if (data === undefined) {
        console.error("Timeout waiting for message to process");
        handleBotResponse("Sorry, I couldn't process your message. Please try again.");
        return;
      }
      if (data.status_code === 200) {
        console.log("Message sent to bot successfully");
        console.log(data);
        setConversationId(data.response.conversation_id);
        handleBotResponse(data.response.answer, data.response.references, data.response.id);
      } else if (data.status_code === 422) {
        console.log("Moderation applied to the message");
        console.log(data);
        handleBotResponse(data.message);
      } else {
        console.log("Error sending message to bot");
        console.log(data);
        handleBotResponse("Sorry, I couldn't process your message. Please try again.");
      }
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      } else {
        console.error("Failed to send message to bot:", error);
        handleBotResponse("Sorry, I couldn't process your message. Please try again.");
      }
    }
  };

  const onNewTopic = () => {
    if (currentItem) {
      clearCachedChatSessionForItem(currentItem);
    }
    resetMessages();
    setUserInput("");
    setConversationId(0);
    var initialMessageText = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
      with the email from ${item.from.displayName}`;
    if (selectedValue !== "" && selectedValue != undefined) {
      initialMessageText += ` in the context of ${selectedValue}`;
    }
    initialMessageText += `. What would you like to do?`;
    addMessage(initialMessageText, "bot");
  };

  const onPostUsage = async (activity, category, meta) => {
    try {
      const data = await postUsage(activity, category, meta, authState.authToken);
      console.log("Usage data posted successfully");
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      } else {
        console.error("Failed to post usage data:", error);
      }
    }
  };

  const getFileUsageContext = () => ({
    mode: selectedTab === "chat" ? "chat" : selectedTab,
    reply: isReplyWindow,
    source: "outlook",
    propertyId: fileContext.propertyId,
  });

  const postDocContextUsage = (files) => {
    try {
      const { mode, reply, source, propertyId } = getFileUsageContext();
      onPostUsage("doc-context", "add-in-use", buildFileUsageMeta({ mode, reply, source, propertyId, files }));
    } catch (e) {
      console.warn("Failed to post usage for doc-context:", e);
    }
  };

  const postUploadUsage = (files, activity = "upload") => {
    try {
      const { mode, reply, source, propertyId } = getFileUsageContext();
      onPostUsage(activity, "add-in-use", buildFileUsageMeta({ mode, reply, source, propertyId, files }));
    } catch (e) {
      console.warn(`Failed to post usage for ${activity}:`, e);
    }
  };

  const fileUpload = useFileUpload(authState?.authToken, fileContext.propertyId, {
    onUploadSuccess: () => fileContext.loadResources(),
    onUploadUsage: postUploadUsage,
  });

  const buildReferenceUsageMeta = (reference) => ({
    mode: "compose",
    reply: false,
    detail: reference?.name || "",
    source: "doutlookcompose",
    tokens: 0,
    property_id: 0,
    prompt_tokens: 0,
    completion_tokens: 0,
  });

  const handlePostReferenceUsage = (activity, reference) => {
    try {
      onPostUsage(activity, "add-in-use", buildReferenceUsageMeta(reference));
    } catch (e) {
      console.warn(`Failed to post usage for ${activity}:`, e);
    }
  };

  const handleViewResource = async (resource) => {
    try {
      await openResourceView(resource, authState?.authToken);
      handlePostReferenceUsage("reference-view", buildResourceReference(resource));
    } catch (error) {
      console.error("Failed to open file view:", error);
    }
  };

  const handleFeedback = (messageId, feedback) => {
    // Send feedback to the backend
    postFeedback(messageId, feedback, authState.authToken);
  };

  const handleSubmitFeedback = (feedback) => {
    handleFeedback(feedbackMessageId, feedback);
    setIsFeedbackOpen(false);
  };

  const handleBotResponseSuccess = (data) => {
    if (Array.isArray(data.response.references) && data.response.references.length > 0) {
      handleBotResponse(
        "I finished generating the draft and used the following data in the process:",
        data.response.references,
        data.response.id
      );
    } else {
      handleBotResponse("I finished generating the draft.", [], data.response.id);
    }
  };

  const handleBotResponseFailure = (data) => {
    if (data.response?.answer) {
      handleBotResponse(data.response.answer, [], data.response.id);
    } else {
      handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
    }
  };

  const handleDraftError = (error) => {
    console.error("Error while generating draft:", error);
    handleBotResponse("Sorry, I couldn't process creating a draft. Please try again.");
  };

  const handleDraftFromEmail = async (additionalContext) => {
    try {
      setIsLoading(true);
      const bodyTypeStr = await host.getBodyType("handleDraftFromEmail");
      const bodyType = bodyTypeStr === "html" ? "html" : "text";
      console.log("Body type:", bodyType);

      const context = {
        sender: item.sender?.displayName,
        recepient: item.to?.[0]?.displayName || "",
        subject: item.subject,
        property: selectedOptionsRef.current[0],
        additionalContext,
        bodyType,
      };

      const emailBodyText = await host.getBodyText("handleDraftFromEmail");
      const textContent = formatEmailText(context.sender, context.recepient, context.subject, emailBodyText);

      const data = await postUserMessage(
        context.property,
        conversationId,
        context.additionalContext,
        authState.authToken,
        "outlookreply",
        selectedTab,
        textContent,
        host.getUserDisplayName(),
        (item.from?.displayName === host.getUserDisplayName()
          ? item.to?.[0]?.displayName
          : item.from?.displayName) || "",
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );

      if (data.status_code === 200) {
        console.log("Message sent to bot successfully");
        console.log(data);
        setConversationId(data.response.conversation_id);

        if (host.isMessageItem()) {
          var finalContent = cleanDraftText(data.response.answer, "OUTLOOK", bodyType);
          localStorage.setItem(
            "draftContext",
            JSON.stringify({
              conversationId: data.response.conversation_id,
              officeConversationId: item.conversationId,
              internetConversationId: item.internetMessageId,
              sessionKey: item.internetMessageId || item.conversationId || "",
              references: data.response.references,
              subject: item.subject,
              propertyId: (selectedOptionsRef.current && selectedOptionsRef.current[0]) || 0,
              propertyText: selectedValue || "",
              chatSession: null,
            })
          );

          const maxMessageId = messages.reduce((max, message) => Math.max(max, message.id || 0), 0);
          const finishMessages = serializeMessagesForCache(messages);
          if (Array.isArray(data.response.references) && data.response.references.length > 0) {
            const finishText = "I finished generating the draft and used the following data in the process:";
            finishMessages.push({
              id: maxMessageId + 1,
              text: finishText,
              sender: "bot",
              links: data.response.references,
              messageId: data.response.id,
              timestamp: new Date().toISOString(),
            });
            await addMessage(finishText, "bot", { links: data.response.references, messageId: data.response.id });
          } else {
            const finishText = "I finished generating the draft.";
            finishMessages.push({
              id: maxMessageId + 1,
              text: finishText,
              sender: "bot",
              messageId: data.response.id,
              timestamp: new Date().toISOString(),
            });
            await addMessage(finishText, "bot", { messageId: data.response.id });
          }

          const chatSession = {
            messages: finishMessages,
            conversationId: data.response.conversation_id,
            selectedOptions: selectedOptionsRef.current,
            selectedValue,
            selectedTab,
            searchChecked,
          };

          try {
            const draftContext = JSON.parse(localStorage.getItem("draftContext") || "{}");
            draftContext.chatSession = chatSession;
            localStorage.setItem("draftContext", JSON.stringify(draftContext));
          } catch {
            // ignore
          }

          if (currentItem) {
            setCachedChatSessionForItem(currentItem, chatSession);
          }

          if (selectedTab === "reply") {
            host.openReplyForm(finalContent);
          } else if (selectedTab === "replyall") {
            console.log("Final content before reply all:", finalContent);
            host.openReplyAllForm(finalContent);
          }
          clearInstructionInput();
        } else {
          handleBotResponseSuccess(data);
        }
      } else if (data.status_code === 422) {
        console.log("Moderation applied to the message");
        console.log(data);
        handleBotResponse(data.message.message.content);
      } else {
        console.log("Error sending message to bot");
        console.log(data);
        handleBotResponseFailure(data);
      }
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      } else {
        handleDraftError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const injectDraftContent = async (data) => {
    const { answer, conversation_id } = data.response;
    console.log("Injecting draft content:", answer);
    setConversationId(conversation_id);
    const { subjectLine, remainingText } = extractSubjectAndContent(answer);
    const bodyTypeStr = await host.getBodyType("injectDraftResponse");
    const bodyType = bodyTypeStr === "html" ? "html" : "text";
    const finalContent = cleanDraftText(remainingText, "OUTLOOK", bodyType);
    console.log("Final content:", finalContent);

    await host.setBody(finalContent);

    if (subjectLine) {
      await host.setSubject(subjectLine);
    }
  };
  const determineModeAndContext = (emailBody, emailSubject, sender, recepient) => {
    let mode = "compose";
    let emailContext = "";
    console.log("Email subject:", emailSubject);
    console.log("Email body:", emailBody);
    if (emailSubject.startsWith("RE:") || emailSubject.startsWith("Re:")) {
      mode = "reply";
    } else if (emailSubject.startsWith("FW:") || emailSubject.startsWith("Fw:")) {
      mode = "forward";
    }
    if (emailBody) {
      emailContext = formatEmailText(sender, recepient, emailSubject, emailBody, false);

    }
    return { mode, emailContext };
  };

  const getSelectedText = async () => {
    try {
      return await host.getSelectedText();
    } catch (error) {
      console.error("Failed to get selected text:", error);
      return { data: "" };
    }
  };
  const handleDraftFromCompose = async (additionalContext) => {
    try {
      setIsLoading(true);
      console.log("Drafting a new email:", additionalContext);
      console.log("Selected property:", selectedOptionsRef.current[0]);

      const emailBodyText = await host.getBodyText("handleDraftFromCompose");
      console.log("Email body:", emailBodyText);
      const emailSubject = await host.getSubject();
      const sender = host.getUserDisplayName();
      const recepients = await host.getRecipients();
      const recipientList = recepients.to || [];
      console.log("Email recepients:", recepients);
      const recipient = recipientList[0]?.displayName || undefined;

      let { mode, emailContext } = determineModeAndContext(emailBodyText, emailSubject, sender, recipient);
      console.log("Mode:", mode);
      console.log("Email context:", emailContext);

      const canUseGraph = canUseGraphAPI();
      console.log("Can use Graph API:", canUseGraph);

      if (vstoMode && (mode === "reply" || mode === "forward")) {
        try {
          const conversationMessages = await host.getConversationThread();
          if (conversationMessages?.length > 0) {
            emailContext = formatConversationContext(conversationMessages);
          }
        } catch (threadError) {
          console.warn("Failed to get conversation via OOM bridge:", threadError);
        }
      } else if (canUseGraph && (mode === "reply" || mode === "forward") && item.conversationId) {
        try {
          const currentGraphAPIToken = getGraphAPIToken();
          console.log("Using Graph API to get full conversation context");
          console.log("Conversation ID:", item.conversationId);
          console.log("Conversation ID type:", typeof item.conversationId);
          console.log("Graph API token preview:", currentGraphAPIToken.substring(0, 50) + "...");

          // Get the full conversation thread using the conversation ID
          const conversationData = await getEmailConversationThread(currentGraphAPIToken, item.conversationId);
          console.log("Conversation data from Graph API:", conversationData);

          if (conversationData && conversationData.conversation && conversationData.conversation.length > 0) {
            // Use the full conversation context instead of just the current email body
            emailContext = formatConversationContext(conversationData.conversation);
            console.log("Graph API email context length:", emailContext.length);
            console.log("Graph API email context preview:", emailContext.substring(0, 200) + "...");
          } else {
            console.warn("No conversation data found via Graph API");
          }
        } catch (graphError) {
          console.warn("Failed to get conversation via Graph API, falling back to Outlook API:", graphError);
          console.warn("Graph error details:", {
            message: graphError.message,
            name: graphError.name,
            stack: graphError.stack
          });
          // Continue with the original emailContext from Outlook API
        }
      } else {
        console.log("Not using Graph API - canUseGraph:", canUseGraph, "mode:", mode, "conversationId:", item.conversationId);
      }

      const selectedText = await getSelectedText();
      console.log("Selected text:", selectedText);
      const data = await postUserMessage(
        selectedOptionsRef.current[0],
        conversationId,
        additionalContext,
        authState.authToken,
        selectedText && typeof selectedText.data === "string" && selectedText.data.trim() !== ""
          ? "doutlookcompose"
          : "outlookcompose",
        selectedText && typeof selectedText.data === "string" && selectedText.data.trim() !== "" ? "composedoc" : mode,
        selectedText && typeof selectedText.data === "string" && selectedText.data.trim() !== ""
          ? selectedText.data
          : emailContext,
        sender,
        recipient,
        !searchChecked,
        getFileIds(),
        handlePollProgress
      );

      if (data.status_code === 200) {
        console.log("Message sent to bot successfully");
        console.log(data);
        await injectDraftContent(data);
        handleBotResponseSuccess(data);
      } else if (data.status_code === 422) {
        console.log("Moderation applied to the message");
        console.log(data);
        handleBotResponse(data.response.answer);
      } else {
        handleBotResponseFailure(data);
      }
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      } else {
        handleDraftError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDraft = async (additionalContext) => {
    if (isComposeContext) {
      await handleDraftFromCompose(additionalContext);
    } else {
      await handleDraftFromEmail(additionalContext);
    }
  };

  const updateUserTone = async (tone) => {
    try {
      const data = await postUserTone(authState.authToken, tone);
      console.log("User tone updated successfully");
    } catch (error) {
      if (error.message === "Unauthorized") {
        onSignout();
      }
      console.error("Failed to update user tone:", error);
    }
    setCheckedTone(tone);
  };

  // Scroll to the last message when a message is added
  useEffect(() => {
    if (lastMessageRef.current) {
      requestAnimationFrame(() => {
        lastMessageRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
      });
    }
  }, [messages]); // Re-run the effect whenever messages change

  useEffect(() => {
    if (persistGuardRef.current || !currentItem || !isComposeContext) {
      return;
    }
    if (messages.length === 0 && !conversationId) {
      return;
    }
    setCachedChatSessionForItem(currentItem, {
      messages,
      conversationId,
      selectedOptions,
      selectedValue,
      selectedTab,
      searchChecked,
    });
  }, [messages, conversationId, selectedOptions, selectedValue, selectedTab, searchChecked, currentItem, isComposeContext]);

  useEffect(() => {
    if (!isLoading) {
      setLoadingStageText("");
    }
  }, [isLoading]);

  // Track selected text (compose) to drive \"Rewrite\" label when applicable
  useEffect(() => {
    let intervalId;
    const isCompose = host.isComposeMode();
    if (isCompose) {
      const updateSelection = async () => {
        try {
          const sel = await getSelectedText();
          const txt = sel && typeof sel.data === "string" ? sel.data : "";
          setHasSelectionForRewrite(!!(txt && txt.trim() !== ""));
        } catch (e) {
          setHasSelectionForRewrite(false);
        }
      };
      updateSelection();
      intervalId = setInterval(updateSelection, 1000);
    } else {
      setHasSelectionForRewrite(false);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedTab]);

  // Fetch initial data when the component mounts
  useEffect(() => {
    let cancelled = false;
    const isLoadCancelled = () => cancelled;
    const abortedMessageResult = () => ({
      initialMessageText: "",
      handledMessaging: true,
      propertyId: undefined,
      propertyName: undefined,
      propertyLegalDes: undefined,
      residentsData: undefined,
    });

    const generateMessageData = async (
      fromEmails,
      toEmail,
      authToken,
      isReplyWindow,
      documentContent,
      formattedProperties,
      messageId
    ) => {
      const data = await fetchInitialData(fromEmails, toEmail || "", authToken, documentContent || "");

      if (
        data.encodedFromEmail?.length > 0 ||
        data.sending_residents?.length > 0 ||
        data.recipient_properties?.length > 0
      ) {
        const residentsData = data.sending_residents;
        const recepientsData = data.recipient_properties;

        const propertyId =
          recepientsData?.length > 0 ? recepientsData[0].id : residentsData[0]?._resident_unit.property_id;
        const propertyName =
          recepientsData?.length > 0 ? recepientsData[0].name : residentsData[0]?._resident_unit._unit_property.name;
        const propertyLegalDes =
          recepientsData?.length > 0
            ? recepientsData[0].legal_des
            : residentsData[0]?._resident_unit._unit_property.legal_des;

        const selectedResident = residentsData.find(
          (resident) => resident._resident_unit && resident._resident_unit.property_id === propertyId
        );

        let initialMessageText = "";
        if (selectedResident) {
          const unit = selectedResident._resident_unit;
          initialMessageText = isReplyWindow
            ? `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
                                  with the email from ${selectedResident.name} living in ${unit._unit_property.name} \
                                  unit ${unit.name}. What would you like to do?`
            : `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
                                  writing an email to ${selectedResident.name} living in ${unit._unit_property.name} \
                                  unit ${unit.name}. What would you like to do?`;
        } else {
          initialMessageText = isReplyWindow
            ? `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           with the email from ${item?.from?.displayName || ""}`
            : `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           writing an email to ${item?.to?.[0]?.displayName || ""}`;
          if (propertyName) {
            initialMessageText += ` in the context of ${propertyName}`;
          }
          initialMessageText += `. What would you like to do?`;
        }

        // Store context in local cache for this message
        if (messageId) {
          setCachedContext(messageId, {
            propertyId: propertyId || 0,
            text: propertyName ? `${propertyName} (${propertyLegalDes || ""})` : "",
          });
        }

        return {
          initialMessageText,
          propertyId,
          propertyName,
          propertyLegalDes,
          residentsData,
          handledMessaging: false,
        };
      }

      // Fallback: if no immediate context, but chat_request present, poll for property context
      const chatRequestId = data.chat_request;
      if (!chatRequestId) {
        // Store no-context in cache if nothing found
        if (messageId) {
          setCachedContext(messageId, { propertyId: 0, text: "" });
        }
        return {
          initialMessageText: isReplyWindow
            ? `Hello ${authState.user.name}!  I am not sure which property ${item?.from?.displayName || "this sender"} belongs to.\
          How can I help?`
            : `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
        with composing a new email. What would you like to do?`,
          handledMessaging: false,
        };
      }

      // Show UI text while polling and post an immediate message asking user to wait
      if (isLoadCancelled()) {
        return abortedMessageResult();
      }

      setSelectedValue("Identifying property…");

      // Build base greeting per (788-792)
      let baseGreeting = isReplyWindow
        ? `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           with the email from ${item?.from?.displayName || ""}`
        : `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           writing an email to ${item?.to?.[0]?.displayName || ""}`;

      // Append wait notice and display immediately
      const waitMessage = `${baseGreeting}. I am attempting to identify the property from the email context. Please wait...`;
      if (isLoadCancelled()) {
        return abortedMessageResult();
      }
      try {
        await addMessage(waitMessage, "bot", { links: [] });
      } catch (e) {
        console.warn("Failed to add wait message:", e);
      }

      if (isLoadCancelled()) {
        return abortedMessageResult();
      }

      try {
        console.log("Starting property-context polling. chat_request:", chatRequestId);
        const statusResult = await pollUserMessage(
          chatRequestId,
          authToken,
          false,
          undefined,
          isLoadCancelled
        );
        if (isLoadCancelled()) {
          return abortedMessageResult();
        }
        const extractedPropertyId = statusResult?.response?.extract_result?.property_id;
        if (extractedPropertyId) {
          const targetId = extractedPropertyId.toString();
          const propsToSearch = Array.isArray(formattedProperties) && formattedProperties.length > 0 ? formattedProperties : properties;
          const matched = (propsToSearch || []).find((p) => p.key === targetId);
          if (matched) {
            // Update UI selection immediately
            setSelectedOptions([targetId]);
            setSelectedValue(matched.text);

            // Derive name and legal_des from text "Name (legal)"
            let propertyName = matched.text;
            let propertyLegalDes = "";
            const idx = matched.text.lastIndexOf(" (");
            if (idx > -1 && matched.text.endsWith(")")) {
              propertyName = matched.text.substring(0, idx);
              propertyLegalDes = matched.text.substring(idx + 2, matched.text.length - 1);
            }

            // Store in cache
            if (messageId) {
              setCachedContext(messageId, { propertyId: extractedPropertyId, text: matched.text });
            }

            // Post follow-up message with property name and prompt
            const followUp = `This email is related to ${propertyName}. What would you like to do?`;
            if (isLoadCancelled()) {
              return abortedMessageResult();
            }
            try {
              await addMessage(followUp, "bot", { links: [] });
            } catch (e) {
              console.warn("Failed to add follow-up property message:", e);
            }

            return {
              initialMessageText: "",
              propertyId: extractedPropertyId,
              propertyName,
              propertyLegalDes,
              residentsData: undefined,
              handledMessaging: true,
            };
          }
        }
      } catch (e) {
        if (e?.name === "PollingCancelledError" || isLoadCancelled()) {
          return abortedMessageResult();
        }
        console.warn("Fallback polling for property context failed:", e);
      }

      if (isLoadCancelled()) {
        return abortedMessageResult();
      }

      // Default if no property context discovered
      setSelectedValue("");
      if (messageId) {
        setCachedContext(messageId, { propertyId: 0, text: "" });
      }
      // Post follow-up 'unable to identify' message with prompt
      const unableMsg = `I was unable to identify a property for this email. What would you like to do?`;
      if (isLoadCancelled()) {
        return abortedMessageResult();
      }
      try {
        await addMessage(unableMsg, "bot", { links: [] });
      } catch (e) {
        console.warn("Failed to add unable-to-identify message:", e);
      }

      return {
        initialMessageText: "",
        handledMessaging: true,
      };
    };

    const applyReadPropertyContext = async (formattedProperties, run) => {
      if (isLoadCancelled()) {
        return;
      }
      run.log("applyReadPropertyContext START", { item });
      const messageId = item?.internetMessageId;

      const cached = getCachedContext(messageId);
      if (cached) {
        run.log("context cache HIT (read)", { messageId, cached });
        if (cached.propertyId && cached.propertyId > 0) {
          setSelectedOptions([cached.propertyId.toString()]);
          setSelectedValue(cached.text || "");
        } else {
          setSelectedOptions([]);
          setSelectedValue("");
        }

        const propertyNameFromText = (cached.text || "").replace(/ \([^)]*\)$/, "");
        const initialMessageText = propertyNameFromText
          ? `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           with the email from ${item?.from?.displayName || ""} in the context of ${propertyNameFromText}. What would you like to do?`
          : `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you \
           with the email from ${item?.from?.displayName || ""}. What would you like to do?`;

        if (isLoadCancelled()) {
          return;
        }
        await resetMessages();
        await addMessage(initialMessageText, "bot", { links: [] });
        return;
      }

      run.log("context cache MISS (read)", { messageId });

      let graphToken = null;
      if (!vstoMode) {
        run.log("ensuring Graph token for body fallback");
        graphToken = await acquireGraphAPIToken({
          existingToken: getGraphAPIToken(),
          authToken: authState.authToken,
          user: authState.user,
          login,
        });
        if (graphToken && graphToken !== getGraphAPIToken()) {
          onGraphTokenAcquired?.(graphToken);
        }
        run.log("Graph token ready", { available: !!graphToken });
      }

      const fromEmails = [item.from?.emailAddress].filter(Boolean);
      if (item.cc && item.cc.length > 0) {
        item.cc.forEach((cc) => {
          fromEmails.push(cc.emailAddress);
        });
      }

      const officeItem = vstoMode ? null : host.getOfficeItem?.();
      const { documentContent, source: bodySource } = await loadReadModeEmailBodyText(officeItem || item, {
        caller: "applyReadPropertyContext",
        graphToken,
        run,
        host: vstoMode ? host : undefined,
      });
      if (isLoadCancelled()) {
        return;
      }
      run.log("email body loaded (read)", { messageId, bodySource });

      run.log("fetchInitialData START (read)", { messageId });
      const { initialMessageText, propertyId, propertyName, propertyLegalDes, residentsData, handledMessaging } =
        await generateMessageData(
          fromEmails,
          item.to[0]?.emailAddress || "",
          authState.authToken,
          true,
          documentContent,
          formattedProperties,
          messageId
        );

      if (isLoadCancelled()) {
        return;
      }

      if (!handledMessaging && initialMessageText) {
        await resetMessages();
        await addMessage(initialMessageText, "bot", {
          links: [],
        });
      }

      if (isLoadCancelled()) {
        return;
      }

      if (propertyId && propertyName) {
        await setSelectedOptions([propertyId.toString()]);
        await setSelectedValue(`${propertyName} (${propertyLegalDes})`);
      } else {
        setSelectedOptions([]);
        setSelectedValue("");
      }

      if (residentsData) {
        setResidents(residentsData);
      }
    };

    const fetchDataEmailRead = async (formattedProperties, run) => {
      if (isLoadCancelled()) {
        return;
      }
      run.log("fetchDataEmailRead START", { item });
      console.log("Fetching initial data for email:", item?.from?.emailAddress, item?.to?.[0]?.emailAddress || "");
      await applyReadPropertyContext(formattedProperties, run);
    };

    const fetchDataEmailCompose = async (formattedProperties, run, options = {}) => {
      const { suppressWelcome = false, skipDraftReferenceMessages = false } = options;
      const storedContext = localStorage.getItem("draftContext");
      run.log("fetchDataEmailCompose START", {
        item,
        hasDraftContext: storedContext !== null,
        suppressWelcome,
        skipDraftReferenceMessages,
      });
      var localConversationId = 0;
      var references = [];
      var propertyFromDraft = false;
      var draftPropertyText = "";
      const composeSubject = await host.getSubject();
      if (storedContext !== null) {
        const originalContext = JSON.parse(storedContext);
        console.log("Original context:", originalContext);
        console.log("Email subject:", composeSubject);

        // Use the same handoff rules as resolveChatSession (VSTO often lacks inReplyTo).
        const draftMatches = hasDraftContextForItem({
          ...item,
          subject: item?.subject || composeSubject,
          isCompose: true,
        });

        if (draftMatches) {
          console.log("Found matching context:", originalContext.conversationId);
          setConversationId(originalContext.conversationId);
          localConversationId = originalContext.conversationId;
          references = originalContext.references || [];
          // Property from reading-pane draft propagation only
          if (originalContext.propertyId) {
            propertyFromDraft = true;
            const pid = originalContext.propertyId.toString();
            const matched = Array.isArray(formattedProperties) ? formattedProperties.find((p) => p.key === pid) : null;
            if (matched) {
              draftPropertyText = matched.text;
              await setSelectedOptions([pid]);
              await setSelectedValue(matched.text);
            } else if (originalContext.propertyText) {
              draftPropertyText = originalContext.propertyText;
              await setSelectedOptions([pid]);
              await setSelectedValue(originalContext.propertyText);
            } else {
              await setSelectedOptions([pid]);
            }
          }
        } else {
          console.log("items do not match: ", item.conversationId, item.internetMessageId, composeSubject);
        }
      }

      const { to: toList, sender, cc: ccList } = await host.getRecipients();
      const recipientName = toList?.[0]?.displayName || item?.to?.[0]?.displayName || "";
      const senderName = sender?.displayName || item?.from?.displayName || "";
      const isReplyOrForwardCompose = !!(
        item?.inReplyTo ||
        localConversationId ||
        /^(RE|FW):/i.test(composeSubject || "")
      );

      if (!propertyFromDraft && !isReplyOrForwardCompose) {
        if (!suppressWelcome) {
          let intro = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you with composing a new email`;
          if (recipientName) {
            intro += ` to ${recipientName}`;
          }
          intro += `. What would you like to do?`;
          await resetMessages();
          await addMessage(intro, "bot", { links: [] });
        }
      } else if (!propertyFromDraft) {
        const propertyCacheKey = item.entryId || item.inReplyTo || "";
        console.log(
          "Fetching initial data for email:",
          sender?.emailAddress,
          toList?.[0]?.emailAddress || "",
          ccList
        );
        let emails = [sender?.emailAddress].filter(Boolean);
        if (ccList?.length > 0) {
          ccList.forEach((ccEntry) => {
            emails.push(ccEntry.emailAddress);
          });
        }
        const emailBodyText = await host.getBodyText("fetchDataEmailCompose:loadBody");
        const documentContent = formatEmailText(
          sender?.emailAddress,
          toList?.[0]?.emailAddress || "",
          composeSubject,
          emailBodyText || ""
        );

        const { initialMessageText, propertyId, propertyName, propertyLegalDes, residentsData, handledMessaging } =
          await generateMessageData(
            emails,
            toList?.[0]?.emailAddress || "",
            authState.authToken,
            false,
            documentContent,
            formattedProperties,
            propertyCacheKey
          );

        if (!handledMessaging && initialMessageText && !suppressWelcome) {
          await resetMessages();
          await addMessage(initialMessageText, "bot", {
            links: [],
          });
        }
        if (propertyId && propertyName) {
          await setSelectedOptions([propertyId.toString()]);
          await setSelectedValue(`${propertyName} (${propertyLegalDes})`);
        }

        if (residentsData) {
          setResidents(residentsData);
        }
      } else if (!suppressWelcome) {
        // Property came from reading-pane draft, but chat history was not restored —
        // still show the standard compose intro.
        const propertyLabel = (draftPropertyText || "").replace(/\s*\([^)]*\)\s*$/, "") || "";
        let intro = `Hello ${authState.user.name}! I'm your AI assistant and I'm here to help you writing an email`;
        if (recipientName) {
          intro += ` to ${recipientName}`;
        } else if (senderName) {
          intro += ` about the message from ${senderName}`;
        }
        if (propertyLabel) {
          intro += ` in the context of ${propertyLabel}`;
        }
        intro += `. What would you like to do?`;
        await resetMessages();
        await addMessage(intro, "bot", { links: [] });
      }

      if (!skipDraftReferenceMessages && localConversationId !== 0) {
        if (references.length > 0) {
          addMessage("I finished drafting the email and used the following data in the process:", "bot", {
            links: references,
            messageId: localConversationId,
          });
        } else {
          addMessage("I have finished drafting the email.", "bot", { messageId: localConversationId });
        }
      }
    };
    const fetchData = async () => {
      const run = beginFetchDataRun({
        isReplyWindow,
        propsItem: currentItem,
        mailboxItem: currentItem,
      });

      try {
        if (isLoadCancelled()) {
          return;
        }
        const isComposeMode = isComposeContext;
        run.log("initialized", { isComposeMode });

        if (isComposeMode) {
          if (currentItem?.inReplyTo || hasDraftContextForItem(currentItem)) {
            setIsComposeReplyWindow(true);
          }
          const subject = await host.getSubject();
          run.log("compose subject loaded", { subject });
          console.log("Email subject:", subject);
          if (subject.startsWith("RE:") || subject.startsWith("FW:") || subject.startsWith("Re:") || subject.startsWith("Fw:")) {
            setIsComposeReplyWindow(true);
          }
        }
        hasFetchedInitialData.current = true;
        run.log("resetting UI state");
        resetMessages();
        lastMessageRef.current = null;
        setConversationId(0);
        // Avoid persisting empty reset before restoring
        persistGuardRef.current = true;
        setUserInput("");
        setSelectedOptions([]);
        setSelectedValue("");
        setSearchChecked(true);
        if (isComposeMode) {
          setSelectedTab("new");
        } else {
          setSelectedTab("replyall");
        }
        const propData = await fetchProperties(authState.authToken);
        if (isLoadCancelled()) {
          return;
        }
        run.log("fetchProperties complete", { propertyCount: propData?.length ?? 0 });
        if (propData.length > 0) {
          const formattedItems = propData.map((item) => ({
            key: item.id.toString(),
            text: `${item.name} (${item.legal_des})`,
          }));
          console.log(formattedItems);
          await setProperties(formattedItems);

          const cachedSession = isComposeMode ? resolveChatSession(currentItem) : null;
          const restoredComposeMessages = !!(isComposeMode && cachedSession?.messages?.length > 0);
          if (cachedSession?.messages?.length > 0) {
            run.log("chat session cache HIT", {
              contextMessageKey,
              messageCount: cachedSession.messages.length,
            });
            restoreMessages(cachedSession.messages);
            setConversationId(cachedSession.conversationId || 0);
            if (cachedSession.searchChecked !== undefined) {
              setSearchChecked(cachedSession.searchChecked);
            }
            if (cachedSession.selectedTab) {
              setSelectedTab(cachedSession.selectedTab);
            } else {
              setSelectedTab("new");
            }
            setIsComposeReplyWindow(
              !!(currentItem?.inReplyTo || hasDraftContextForItem(currentItem))
            );
            const subject = await host.getSubject();
            if (
              subject.startsWith("RE:") ||
              subject.startsWith("FW:") ||
              subject.startsWith("Re:") ||
              subject.startsWith("Fw:")
            ) {
              setIsComposeReplyWindow(true);
            }
          }

          if (isComposeMode) {
            await fetchDataEmailCompose(formattedItems, run, {
              suppressWelcome: restoredComposeMessages,
              skipDraftReferenceMessages: restoredComposeMessages,
            });
            clearDraftContextForItem(currentItem);
            postUsage("user-activation", "add-in-open", { source: "doutlookcompose" }, authState.authToken);
          } else {
            await fetchDataEmailRead(formattedItems, run);
            postUsage("user-activation", "add-in-open", { source: "outlookreply" }, authState.authToken);
          }
          // Restore saved instruction (if any) for this message after resets
          try {
            const key = getMessageKey();
            if (key) {
              const restored = getCachedInstruction(key);
              if (restored && typeof restored === "string") {
                setUserInput(restored);
              }
            }
          } catch {
            // ignore
          }
          // Re-enable persistence after restoration
          persistGuardRef.current = false;
        }
        run.end("success");
      } catch (error) {
        run.end("error", { error: error?.message || String(error) });
        console.error("Failed to fetch data:", error);
        if (error.message === "Unauthorized") {
          onSignout();
        }
        // TBD: Handle errors here
      }
    };
    console.log("[fetchData] effect triggered", {
      contextMessageKey,
      authTokenPresent: !!authState?.authToken,
      isCompose: !!currentItem?.isCompose,
    });
    // New blank compose often has no subject / message ids yet — still must load properties.
    const hasEmailContext = !!(
      currentItem?.isCompose ||
      contextMessageKey ||
      currentItem?.conversationId ||
      currentItem?.internetMessageId ||
      currentItem?.entryId ||
      currentItem?.subject
    );
    if (authState && authState.authToken && currentItem && hasEmailContext) {
      fetchData();
    }

    return () => {
      cancelled = true;
    };
  }, [authState?.authToken, contextMessageKey, currentItem?.internetMessageId, currentItem?.conversationId, currentItem?.entryId, currentItem?.isCompose]);

  // moved auto-resize behavior to ComposeBox

  // For debug purposes, log the selected options when they change
  useEffect(() => {
    console.log("Selected options:", selectedOptions);
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);

  // Persist instruction text as user types (per-message, session-lifetime via context cache).
  // Empty input clears the cache so a sent instruction cannot come back on email switch.
  useEffect(() => {
    if (!persistGuardRef.current) {
      try {
        const key = getMessageKey();
        if (!key) {
          return;
        }
        if (!userInput) {
          clearCachedInstruction(key);
        } else {
          setCachedInstruction(key, userInput);
        }
      } catch {
        // ignore
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInput]);

  return (
    <div className={styles.chatBot} id="chatbot">
      <div id="pinTooltipPlaceholder" className={styles.pinTooltipPlaceholder}></div>
      <div className={styles.messageList} id="messageList">
        {messages.map((message, index) => (
          <ChatMessage
            message={message}
            key={message.id}
            ref={index === messages.length - 1 ? lastMessageRef : null}
            canAttach={isComposeContext}
            onAttachReference={handleAttachReference}
            onShareReference={handleShareReference}
            onPostReferenceUsage={handlePostReferenceUsage}
          />
        ))}
        <TextDialog
          isOpen={isFeedbackOpen}
          onClose={handleCloseFeedback}
          onSubmit={handleSubmitFeedback}
          title="Provide Feedback"
          text="Please provide more details on what is wrong with the message:"
          label="How can we improve?"
          closeText="Cancel"
          submitText="Submit Feedback"
        />
      </div>
      <ComposeBox
        addinType="OUTLOOK"
        properties={properties}
        selectedOptions={selectedOptions}
        setSelectedOptions={setSelectedOptions}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        onContextChangeExternal={({ key, text }) => {
          try {
            const messageId = getMessageKey();
            if (messageId) {
              const numericId = key ? parseInt(key.toString(), 10) : 0;
              setCachedContext(messageId, { propertyId: numericId || 0, text: text || "" });
            }
          } catch (e) {
            // ignore
          }
        }}
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        userPromptArea={userPromptArea}
        userInput={userInput}
        setUserInput={setUserInput}
        handleSendMessage={handleSendMessage}
        isReplyWindow={isReplyWindow}
        isComposeReplyWindow={isComposeReplyWindow}
        onNewTopic={onNewTopic}
        onSignout={onSignout}
        onPostUsage={onPostUsage}
        checkedTone={checkedTone}
        setCheckedTone={updateUserTone}
        userStyles={userStyles}
        selectedStyleName={selectedStyleName}
        onSelectStyle={updateUserStyle}
        onSearchCheckedChange={onSearchCheckedChange}
        searchChecked={searchChecked}
        hasSelectionForRewrite={hasSelectionForRewrite}
        fileContext={fileContext}
        fileUpload={fileUpload}
        onViewResource={handleViewResource}
      />
      {isLoading && (
        <div className={styles.overlay}>
          <Spinner className={styles.spinner} />
          {loadingStageText && (
            <div style={{ marginTop: 12, textAlign: "center", color: "#616161", fontSize: 12 }}>
              {loadingStageText}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
UnicliChat.propTypes = {
  host: PropTypes.object.isRequired,
  currentItem: PropTypes.object,
  currentUser: PropTypes.object,
  graphAPIToken: PropTypes.string,
  onGraphTokenAcquired: PropTypes.func,
};

export default UnicliChat;
